import './AdditionalPayments.css';
import React, { useState, useEffect, useMemo} from 'react';
import moment from 'moment/moment';
import { requestCreateAdditionalPayment, requestGetAdditionalPayments, requestUpdateAdditionalPaymentStatus } from '../../services/wisdomServices/additionalPaymentService.js';
import { getSessionItem } from '../../utils/helper/session.js';
import { Tag } from 'primereact/tag';
import { WisdomDataTable } from '../../components/dataTable/WisdomDataTable.jsx';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import AdditionalPaymentModal from '../../components/additionalPaymentsModal/AdditionalPaymentModal.jsx';
import { Can } from '../../security/Can.jsx';
import { PERMISSIONS } from '../../security/permissions.generated.js';

/**
 * @function AdditionalPayments
 * @returns {JSX.Element} - additional payments management page
 */
function AdditionalPayments() {
  const [additionalPayments, setAdditionalPayments] = useState([])
  const [loading, setLoading] = useState(false)
  const loggedUser = getSessionItem("User")
  const [dialogOpen, setDialogOpen] = useState(false)
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [filters, setFilters] = useState({
      global: {value:null, matchMode: FilterMatchMode.CONTAINS},
      userName: {value:null, matchMode: FilterMatchMode.CONTAINS}
  })
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const globalFilters = ['submited_by', 'userName']
  const onGlobalFilterChange = (e) => {
      const value = e.target.value;
      let _filters = { ...filters };

      _filters['global'].value = value;

      setFilters(_filters);
      setGlobalFilterValue(value);
  };

  const renderHeader = () => {
    return (
        <div className="manager-additional-payment-header">
          <div>
              <Button label='Add Payment' severity="info" onClick={() => setDialogOpen(true)}/>
          </div>
          <div>
            <IconField iconPosition="left">
                <InputIcon className="pi pi-search" />
                <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Keyword Search" />
            </IconField>
          </div>
        </div>
    );
  };

  const header = renderHeader();

  const storedProjects = JSON.parse(sessionStorage.getItem("projects") || "[]");
  /**
   * @async
   * @function getAdditionalPayments
   * @description - fetches additional payment requests from the API based on the user's role
   */
  const getAdditionalPayments = async(pageNumber = 1, pageSize = 25) => {
    try {
      setLoading(true)
      let response = await requestGetAdditionalPayments(pageNumber, pageSize)
      const { data, pagination } = response;
      let payments = [];
      for (const item of data) {
        let payment = {
          id: item.id,
          userID : item.userId,
          userName: item.userName,
          userEmail: item.userEmail,
          reason: item.reason,
          amount: item.amount,
          status: item.status,
          created_at: moment(item.created_at).format("YYYY-MM-DD HH:mm:ss"),
          comment: item.comment
        }
        payments.push(payment);
        
      }
      setAdditionalPayments(payments);
      setTotalRecords(pagination.totalResults);
      
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const onPageChange = (event) => {
    const newPage = event.page + 1; 
    const newRows = event.rows;

    setPage(newPage);
    setRows(newRows);
    setFirst(event.first);

    getAdditionalPayments(newPage, newRows);
  };

  const getSeverity = (status) => {
      switch (status) {
          case 'Approved':
              return 'success';

          case 'Rejected':
              return 'danger';

          case 'Cancelled':
              return 'warning';

          case 'Pending Approval':
              return 'info';

          default:
              return null;
      }
  };

  const statusTemplate = (row) => {
    if(row.status === "Pending Approval"){
      return(
        <div className='manager-additional-payments-btns-box'>
          <Tag  value="Pending" severity={getSeverity(row.status)}/>
          <Can permission={PERMISSIONS.additionalPaymentsByIdStatus_patch}>
            <Tag className='manager-additional-payments-btn' onClick={() => cancelPaymentRequest(row.id)} value="Cancel" severity={'warning'}/> 

          </Can>
        </div>

      )
    }else{
      return (
        <Tag  value={row.status} severity={getSeverity(row.status)}/>
      )

    }
  }

  const formatCurrency = (value) => {
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  const priceBodyTemplate = (rowData) => {
    return formatCurrency(rowData.amount);
  };
  
  const columns = useMemo(() => ([
    {
      field: 'userName',
      header: 'Name'
    },
    {
      field: 'reason',
      header: 'Reason'
    },
    {
      header: 'Amount',
      body: priceBodyTemplate
    },
    {
      field: 'comment',
      header: 'Comment'
    },
    {
      header: 'Status',
      body: statusTemplate
    },
    {
      field: 'created_at',
      header: 'Created At'
    },
  ]), []);
  
  /**
   * @function cancelPaymentRequest
   * @param {Object} requestData 
   * @description - cancels an existing additional payment request
   */
  let cancelPaymentRequest=  async(requestId) => {
    let keys = {
      status: "Cancelled"
    };

    await requestUpdateAdditionalPaymentStatus(requestId, keys)
    await getAdditionalPayments()
  };


  /**
   * @async
   * @function addPayment
   * @param {Object} additionalPayment 
   * @description - submits a new additional payment request
   */
  const addPayment = async(additionalPayment) => {
    try {
      setLoading(true)
      let keys = {
        userId : additionalPayment.userID,
        managerId: loggedUser.id,
        reason : additionalPayment.reason,
        amount : additionalPayment.amount,
        comment: additionalPayment.comment
      }
      await requestCreateAdditionalPayment(keys)
      
    } catch (error) {
      console.error(error)
      
    } finally {
      setLoading(false)
      setDialogOpen(false)
      getAdditionalPayments();
    }
  }

  useEffect(() => {
    getAdditionalPayments(page, 25)
  },[]);
  
  if (!storedProjects || storedProjects.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
          textAlign: "center",
        }}
      >
        <h1 style={{ color: "#999", fontWeight: 400 }}>
          You don’t have any assigned projects yet to access <strong>Additional Payments</strong> page.
        </h1>
      </div>
    );
  }

  return (
    
    <div className='manager-additional-payment-page-container'>
      <div>
        <div>
           <h1 className="dashboardPayrollMain-header">Additional Payments</h1>
        </div>
        <div>
          <WisdomDataTable
            data={additionalPayments}
            columns={columns}
            onSubmit={addPayment}
            header={header}
            filters={filters}
            globalFilters={globalFilters}
            loading={loading}
            totalRecords={totalRecords}
            onPage={onPageChange}
            first={first}
            rows={rows}
          />
        </div>
      </div>
      {dialogOpen && <AdditionalPaymentModal loading={loading} onCancel={() => setDialogOpen(false)} addPayment={addPayment} />} 
    </div>
  );
}

export default AdditionalPayments;
